import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { EASE_LUSION } from './easing';
import { useSurfaceIsDark } from './useSurfaceIsDark';

interface PreloaderProps {
  /** true once the reel has real frames */
  ready: boolean;
  /**
   * The last moment the page is completely hidden — fired a beat before the
   * sheet starts to lift. Anything that must not be SEEN changing belongs
   * here and not on onDone: the sheet wipes upward, so by the time onDone
   * fires the top of the page has been on screen for most of a second.
   */
  onBeforeLift?: () => void;
  onDone?: () => void;
  /** hide the mark while the reel is in theatre mode */
  chromeHidden?: boolean;
  /** the mark doubles as the home button once it has landed */
  onLogoClick?: () => void;
}

type Phase = 'loading' | 'mark' | 'name' | 'fly' | 'gone';

const REST = 'amlet Hayrapetyan';

/**
 * The progress bar is the crossbar of an H: when loading completes the bar
 * draws its two stems, the rest of the name extends from it, and the lockup
 * flies into the header.
 *
 * It is never unmounted — once landed it simply *is* the site logo, which is
 * why the header renders only an invisible anchor in that slot.
 */
/** when the sheet begins its upward wipe, on beginChoreography's clock */
const LIFT_AT = 1650;
/**
 * The cue goes out ahead of the wipe rather than on it. The hero reel fills
 * almost the whole viewport, so the wipe's edge crosses the BOTTOM of it about
 * 60ms after it starts moving — three frames, which is no margin at all to
 * land anything in. This head start is the part of the margin that does not
 * depend on how the hero happens to be laid out.
 */
const CUE_AT = LIFT_AT - 150;

const Preloader: React.FC<PreloaderProps> = ({ ready, onBeforeLift, onDone, chromeHidden = false, onLogoClick }) => {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<Phase>('loading');
  const [flight, setFlight] = useState<{ x: number; y: number; s: number } | null>(null);
  const [nameWidth, setNameWidth] = useState(0);
  // The lockup is 522px wide at full size, which overflows a 390px phone on
  // both sides. Its dimensions are animated numbers, not classes, so the
  // breakpoint has to exist in JS as well as CSS.
  const [compact, setCompact] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640
  );
  const lockRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const advanced = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  // beginChoreography is called from the rAF loop below, which closes over the
  // first render, so anything it fires has to come through a ref.
  const onBeforeLiftRef = useRef(onBeforeLift);
  onBeforeLiftRef.current = onBeforeLift;

  useEffect(() => {
    const onResize = () => setCompact(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // measure the name once webfonts have settled
  useEffect(() => {
    const measure = () => {
      const w = nameRef.current?.scrollWidth;
      if (w) setNameWidth(w);
    };
    measure();
    document.fonts?.ready.then(measure).catch(() => {});
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // The mark colours itself by what is actually painted beneath it — the same
  // sampling the nav pills use, so both flip together on the same surfaces.
  const overDark = useSurfaceIsDark(() => {
    const r = lockRef.current?.getBoundingClientRect();
    return r ? { x: r.left + Math.min(30, r.width / 2), y: r.top + r.height / 2 } : null;
  }, false);

  const computeFlight = () => {
    const lock = lockRef.current;
    const nav = document.getElementById('nav-logo');
    if (!lock || !nav) return;
    // measure the lockup at rest, without whatever transform is applied
    const prev = lock.style.transform;
    lock.style.transform = 'none';
    const lr = lock.getBoundingClientRect();
    lock.style.transform = prev;
    const nr = nav.getBoundingClientRect();
    if (!lr.height || !nr.height) return;
    const s = nr.height / lr.height;
    // Centre on the anchor rather than share its left edge. The anchor is the
    // name in the nav's font size; the lockup carries the drawn H as well, so
    // the two are not the same width, and left-aligning them would land the
    // logo a few pixels off the centre line the header now puts the anchor on.
    // transform-origin is 0 0, so the scaled lockup's centre is left + w*s/2.
    setFlight({ x: nr.left + nr.width / 2 - (lr.left + (lr.width * s) / 2), y: nr.top - lr.top, s });
  };

  const beginChoreography = () => {
    if (advanced.current) return;
    advanced.current = true;
    const t = timersRef.current;
    t.push(setTimeout(() => setPhase('mark'), 260));
    t.push(setTimeout(() => setPhase('name'), 900));
    // last call before anything is uncovered
    t.push(setTimeout(() => onBeforeLiftRef.current?.(), CUE_AT));
    t.push(
      setTimeout(() => {
        computeFlight();
        setPhase('fly');
      }, LIFT_AT)
    );
    // the nav fades in under the still-visible lockup, then the sheet clears
    t.push(setTimeout(() => onDoneRef.current?.(), 2450));
    t.push(setTimeout(() => setPhase('gone'), 3050));
  };

  useEffect(() => {
    const start = performance.now();
    let readyAt: number | null = null;
    let raf = 0;
    const tick = () => {
      const now = performance.now();
      const base = Math.min(90, ((now - start) / 1500) * 90);
      let p = base;
      if (ready) {
        if (readyAt === null) readyAt = now;
        p = Math.max(base, 90 + Math.min(1, (now - readyAt) / 400) * 10);
      }
      setProgress(p);
      if (p >= 99.5) beginChoreography();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  // the loader must never be what keeps someone off the site
  useEffect(() => {
    const failsafe = setTimeout(() => {
      setProgress(100);
      beginChoreography();
    }, 6000);
    return () => clearTimeout(failsafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep the landed mark pinned to the header if the window changes size
  useEffect(() => {
    if (phase !== 'fly' && phase !== 'gone') return;
    const onResize = () => computeFlight();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const shown = Math.round(progress);
  const isMark = phase !== 'loading';
  const showName = phase === 'name' || phase === 'fly' || phase === 'gone';
  const flying = phase === 'fly' || phase === 'gone';
  const landed = phase === 'gone';

  return (
    <>
      {/* the sheet lifts as the lockup flies, revealing the page beneath */}
      <AnimatePresence>
        {!flying && (
          <motion.div
            // tagged light so the occlusion-aware sampler keeps the logo ink
            // until this sheet's bottom-up wipe edge passes it
            data-surface="light"
            className="fixed inset-0 z-[190] bg-paper"
            exit={{ y: '-100%' }}
            transition={{ duration: 0.85, ease: EASE_LUSION }}
          />
        )}
      </AnimatePresence>

      {/* counter, retired once the mark forms */}
      <AnimatePresence>
        {!flying && (
          <motion.div
            className="fixed inset-0 z-[195] pointer-events-none"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="absolute left-5 md:left-10 bottom-4 md:bottom-8 overflow-hidden h-[64px] md:h-[92px] select-none"
              animate={{ opacity: isMark ? 0 : 1 }}
              transition={{ duration: 0.35 }}
              aria-hidden="true"
            >
              <motion.div
                animate={{ y: `${-shown * (100 / 101)}%` }}
                transition={{ ease: 'linear', duration: 0.15 }}
                className="will-change-transform"
              >
                {Array.from({ length: 101 }, (_, i) => (
                  <div
                    key={i}
                    className="h-[64px] md:h-[92px] flex items-center font-grotesk font-medium text-[56px] md:text-[84px] leading-none text-ink tabular-nums"
                  >
                    {i.toString().padStart(3, '0')}
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              className="hidden sm:block absolute right-5 md:right-10 bottom-6 md:bottom-12 font-grotesk text-[10px] md:text-xs uppercase tracking-[0.3em] text-ink/50"
              animate={{ opacity: isMark ? 0 : 1 }}
              transition={{ duration: 0.35 }}
            >
              Showreel 2026
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* The lockup. Never unmounted: after the flight this is the site logo.
          It must sit ABOVE the loading sheet (z-190) while it plays, then drop
          to logo level so the menu overlay can cover it. */}
      <motion.div
        className={`fixed inset-0 ${landed ? 'z-[100]' : 'z-[200]'}`}
        style={{
          pointerEvents: 'none',
          // The blend has to live here, not on the mark. mix-blend-mode composites
          // against the nearest ancestor stacking context, and this fixed z-indexed
          // wrapper is one: applied to the mark it would blend against this div's
          // empty group and just come out white.
          mixBlendMode: landed ? 'difference' : undefined,
        }}
        animate={{ opacity: chromeHidden ? 0 : 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            ref={lockRef}
            // Once landed the mark is painted white and inverted against
            // whatever is behind it (see the wrapper), which is why it carries
            // no halo, no chip and no colour flip any more.
            //
            // The flip it used to do could not work here. useSurfaceIsDark reads
            // a data-surface tag rather than luminance, and the work cards carry
            // no tag, so the mark went ink over every one of them: fine on the
            // bright ones, invisible on the dark ones, and the paper halo that
            // used to paper over that is exactly what was asked to go.
            //
            // Inverting is per-pixel and needs to know nothing. Its own failure
            // case is a backdrop at mid-grey, which this palette does not have:
            // paper is #E9EAF0 and card is #0C0D14.
            className={`flex items-end transition-[color] duration-150 ${
              landed ? 'text-white' : overDark ? 'text-white' : 'text-ink'
            }`}
            style={{ transformOrigin: '0 0', pointerEvents: landed && !chromeHidden ? 'auto' : 'none' }}
            // The name used to animate its WIDTH from 0, which changed the
            // lockup's box on every frame; the flex centring above then
            // repositioned it, and Lighthouse recorded 15 layout shifts. The
            // name now holds its final width and the lockup slides by half of
            // it instead, so the bar still starts centred but the movement is
            // a composited transform that cannot shift layout.
            animate={
              flying && flight
                ? { x: flight.x, y: flight.y, scale: flight.s }
                : { x: showName ? 0 : nameWidth / 2, y: 0, scale: 1 }
            }
            transition={{ duration: flying ? 0.95 : 0.7, ease: EASE_LUSION }}
            onClick={() => landed && onLogoClick?.()}
            role={landed ? 'button' : undefined}
            data-cursor={landed ? 'TOP' : undefined}
          >
            {/* The name is one <span> per letter so it can stagger in, and
                axe cannot reassemble split text: it read the rendered letters
                as "amlet Hayrapetyan", compared them with an aria-label of
                "Hamlet Hayrapetyan, back to top" and reported a WCAG 2.5.3
                mismatch. aria-hidden on the letters does not help, because
                that rule measures what is on SCREEN, not what reaches the
                a11y tree. Naming the control from a visually hidden child
                instead makes the rule inapplicable — it only fires on an
                explicit label — and the accessible name is byte-identical.
                Checked against the axe-core build Lighthouse ships. */}
            {landed && <span className="sr-only">Hamlet Hayrapetyan, back to top</span>}
            {/* the H, built so the loading bar becomes its crossbar */}
            <motion.div
              className="relative shrink-0 mb-[7px] sm:mb-[11px]"
              animate={{
                width: isMark ? (compact ? 23 : 36) : compact ? 132 : 210,
                height: isMark ? (compact ? 26 : 40) : 4,
              }}
              transition={{ duration: 0.7, ease: EASE_LUSION }}
            >
              <motion.div
                className="absolute left-0 top-0 h-full bg-current"
                animate={{ opacity: isMark ? 1 : 0, width: isMark ? (compact ? 6 : 9) : 4 }}
                transition={{ duration: 0.55, ease: EASE_LUSION, delay: isMark ? 0.12 : 0 }}
              />
              <motion.div
                className="absolute right-0 top-0 h-full bg-current"
                animate={{ opacity: isMark ? 1 : 0, width: isMark ? (compact ? 6 : 9) : 4 }}
                transition={{ duration: 0.55, ease: EASE_LUSION, delay: isMark ? 0.12 : 0 }}
              />
              <motion.div
                className="absolute left-0 w-full overflow-hidden bg-current/10"
                style={{ top: '50%', translateY: '-50%' }}
                animate={{ height: isMark ? (compact ? 6 : 9) : 4 }}
                transition={{ duration: 0.7, ease: EASE_LUSION }}
              >
                <motion.div className="h-full bg-current origin-left" style={{ scaleX: progress / 100 }} />
              </motion.div>
            </motion.div>

            {/* the name takes no width until it appears, or it would push the
                loading bar off centre */}
            <motion.div
              // clip-path only ever trims horizontally, so descenders (y, p)
              // survive without the pb/-mb shim the overflow mask needed.
              style={{ width: nameWidth || 'auto' }}
              animate={{ clipPath: showName ? 'inset(0 0% 0 0)' : 'inset(0 100% 0 0)' }}
              transition={{ duration: 0.7, ease: EASE_LUSION }}
            >
              <div ref={nameRef} aria-hidden="true" className="flex pl-1">
                {REST.split('').map((ch, i) => (
                  <motion.span
                    key={i}
                    // leading must leave room for descenders (y, p) inside the
                    // overflow mask, or they get sliced off
                    className="font-grotesk font-semibold leading-[1.12] text-[34px] sm:text-[54px] whitespace-pre"
                    initial={{ y: '110%', opacity: 0 }}
                    animate={showName ? { y: '0%', opacity: 1 } : { y: '110%', opacity: 0 }}
                    transition={{ duration: 0.55, delay: showName ? i * 0.018 : 0, ease: EASE_LUSION }}
                  >
                    {ch}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
};

export default Preloader;
